import React from 'react';
import styled from 'styled-components';
import type { DocumentSummaryStats } from '../types';
import { formatNumber } from '../utils/format';
import { formatDateTime } from '../utils/time';
import { ActionsMenu, type PageActionEntry } from './documentInspect/ActionsMenu';

export interface SummaryAction {
    id: string;
    label: string;
    onClick?: () => void;
    icon?: React.ReactNode;
    variant?: 'primary' | 'secondary';
    disabled?: boolean;
}

interface DocumentSummaryHeaderProps {
    title: string;
    subtitle?: string;
    summary: DocumentSummaryStats;
    reviewStatusLabel?: string;
    lastUpdatedAt?: string;
    lastUpdatedBy?: string;
    actions?: SummaryAction[];
    datasetPath?: string;
    onDuplicatesClick?: () => void;
    filevineProjectId?: number | null;
    filevineProjectName?: string | null;
}

const HeaderContainer = styled.section`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const TitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 24px;
`;

const TitleBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const Title = styled.h2`
    font-size: 22px;
    font-weight: 600;
    margin: 0;
    color: #111;
    letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
    margin: 0;
    font-size: 13px;
    color: #999;
`;

const MetaRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    font-size: 13px;
    color: #999;
`;

const FilevineBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    border-radius: 10px;
    background: #eef2ff;
    color: #4338ca;
    font-size: 12px;
    font-weight: 500;
`;

const Actions = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-self: flex-start;
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 2fr;
    gap: 1px;
    background: #f0f0f0;
    border-radius: 8px;
    overflow: hidden;
`;

const StatCell = styled.div`
    background: #fff;
    padding: 16px 20px;
`;

const StatLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #999;
    margin-bottom: 4px;
`;

const StatValue = styled.div`
    font-size: 22px;
    font-weight: 600;
    color: #111;
    font-variant-numeric: tabular-nums;
`;

const ProgressCell = styled.div`
    background: #fff;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const ProgressHeader = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 6px;
`;

const ProgressLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #999;
`;

const ProgressPercent = styled.span`
    font-size: 12px;
    font-weight: 500;
    color: #666;
`;

const ProgressTrack = styled.div`
    width: 100%;
    height: 6px;
    background: #f0f0f0;
    border-radius: 3px;
    overflow: hidden;
`;

const ProgressFill = styled.div<{ $value: number }>`
    height: 100%;
    width: ${props => `${Math.max(props.$value, 0.5)}%`};
    background: ${props => (props.$value > 0 ? '#22c55e' : '#e0e0e0')};
    border-radius: 3px;
    transition: width 0.3s ease;
`;

export const DocumentSummaryHeader: React.FC<DocumentSummaryHeaderProps> = ({
    title,
    subtitle,
    summary,
    reviewStatusLabel,
    lastUpdatedAt,
    lastUpdatedBy,
    actions = [],
    datasetPath: _datasetPath,
    onDuplicatesClick,
    filevineProjectId,
    filevineProjectName,
}) => {
    const progressPercent = Math.round(summary.reviewCompletion * 100);
    const formattedUpdatedAt = formatDateTime(lastUpdatedAt ?? null, undefined, '');

    const isFilevineLinked = typeof filevineProjectId === 'number';
    const hasTitle = Boolean(title);
    const hasMeta = Boolean(formattedUpdatedAt || lastUpdatedBy || isFilevineLinked);
    const hasTitleRow = hasTitle || subtitle || hasMeta || actions.length > 0;

    return (
        <HeaderContainer>
            {hasTitleRow && (
            <TitleRow>
                <TitleBlock>
                    {hasTitle && <Title>{title}</Title>}
                    {subtitle && <Subtitle>{subtitle}</Subtitle>}
                    {hasMeta && (
                        <MetaRow>
                            {formattedUpdatedAt ? <span>Updated {formattedUpdatedAt}</span> : null}
                            {lastUpdatedBy && <span>by {lastUpdatedBy}</span>}
                            {isFilevineLinked && (
                                <FilevineBadge title={`Filevine project ${filevineProjectId}`}>
                                    Filevine: {filevineProjectName || `project ${filevineProjectId}`}
                                </FilevineBadge>
                            )}
                        </MetaRow>
                    )}
                </TitleBlock>
                {actions.length > 0 && (
                    <Actions>
                        <ActionsMenu
                            config={{
                                items: actions.map((action): PageActionEntry => ({
                                    key: action.id,
                                    label: action.label,
                                    icon: action.icon,
                                    disabled: action.disabled,
                                    onSelect: () => action.onClick?.(),
                                })),
                            }}
                        />
                    </Actions>
                )}
            </TitleRow>
            )}

            <StatsGrid>
                <StatCell>
                    <StatLabel>Documents</StatLabel>
                    <StatValue>{formatNumber(summary.totalDocuments)}</StatValue>
                </StatCell>
                <StatCell>
                    <StatLabel>Pages</StatLabel>
                    <StatValue>{formatNumber(summary.totalPages)}</StatValue>
                </StatCell>
                <ProgressCell>
                    <ProgressHeader>
                        <ProgressLabel>{reviewStatusLabel ?? 'Reviewed'}</ProgressLabel>
                        <ProgressPercent>{progressPercent}%</ProgressPercent>
                    </ProgressHeader>
                    <ProgressTrack>
                        <ProgressFill $value={progressPercent} />
                    </ProgressTrack>
                </ProgressCell>
            </StatsGrid>
        </HeaderContainer>
    );
};
